import React, { useState, useEffect, useCallback } from "react";

import {
  listIgnitionGateways,
  createIgnitionGateway,
  updateIgnitionGateway,
  deleteIgnitionGateway,
  getIgnitionGatewayState,
  getIgnitionGatewayTagCount,
  ignitionAgentUrl,
  generateIgnitionApiKey,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import Select from "../../../../components/Select";
import plusIcon from "../../../../assets/plus.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import moreIcon from "../../../../assets/more-horiz.svg";
import {
  DEFAULT_HISTORY_TEMPLATE,
  HistorianDefaultsFields,
  buildHistorianTemplatePayload,
  templateFormFromRecord,
} from "./historian-defaults";

const POLL_INTERVAL = 10000;

const HISTORY_MODE_OPTIONS = [
  { value: "stored", label: "Stored in Control Seat" },
  { value: "federated", label: "Federated from gateway" },
];

const STATUS_COLORS = {
  online: { bg: "rgba(76,175,80,0.12)", color: "rgba(76,175,80,0.95)", border: "rgba(76,175,80,0.25)", dot: "#4caf50" },
  connecting: { bg: "rgba(255,193,7,0.12)", color: "rgba(255,193,7,0.95)", border: "rgba(255,193,7,0.25)", dot: "#ffc107" },
  offline: { bg: "rgba(158,158,158,0.12)", color: "rgba(200,200,200,0.95)", border: "rgba(158,158,158,0.25)", dot: "#9e9e9e" },
  error: { bg: "rgba(244,67,54,0.12)", color: "rgba(244,67,54,0.95)", border: "rgba(244,67,54,0.25)", dot: "#f44336" },
};

const StatusDot = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.offline;
  return (
    <span title={status} style={{ width: 8, height: 8, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
  );
};

const errorMessage = (err, fb) => {
  const m = String(err?.message || err).trim();
  return m || fb;
};

const formatLastConnected = (iso) => {
  if (!iso) return "Never connected";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Never connected";
  return `Last connected ${d.toLocaleString()}`;
};

// ── Connection-token reveal (shown once on create / rotate) ───────────

const CopyRow = ({ label, hint, value }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard?.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard blocked (insecure context); the value is still selectable
    }
  };
  return (
    <div className="gwField">
      <label className="gwLabel">
        {label} <span style={{ color: "var(--gw-muted)", fontWeight: 400 }}>({hint})</span>
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          className="gwInput"
          readOnly
          value={value}
          onFocus={(e) => e.target.select()}
          style={{ flex: 1, minWidth: 0, fontFamily: "monospace", fontSize: 12 }}
        />
        <button className="gwGhostBtn" type="button" onClick={copy} style={{ flexShrink: 0 }}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
};

const TokenReveal = ({ title, subtitle, apiKey, onClose }) => (
  <div className="gwOverlay" onClick={onClose}>
    <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 580 }}>
      <div className="gwModalHeader">
        <div>
          <div className="gwModalTitle">{title}</div>
          <div className="gwModalSubtitle">{subtitle}</div>
        </div>
        <button className="gwModalClose" type="button" onClick={onClose}>
          <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
        </button>
      </div>
      <div className="gwModalBody">
        <CopyRow label="Agent URL" hint="controlseat.csUrl" value={ignitionAgentUrl()} />
        <CopyRow label="API key" hint="controlseat.apiKey" value={apiKey} />
        <div className="gwHint" style={{ marginTop: 4 }}>
          Set these two values in the Control Seat module on your Ignition gateway — either in
          <code> controlseat-config.json</code> or as <code>-Dcontrolseat.*</code> JVM arguments. The module
          dials in and streams its tags automatically. <strong>The API key is shown only once</strong>; if you
          lose it, rotate the key to issue a new one.
        </div>
      </div>
      <div className="gwModalFooter">
        <button className="gwPrimaryBtn" type="button" onClick={onClose}>Done</button>
      </div>
    </div>
  </div>
);

// ── Add gateway (name → token reveal) ─────────────────────────────────

const INITIAL_GATEWAY_FORM = {
  name: "",
  historyMode: "stored",
  historianEnabled: true,
  ...DEFAULT_HISTORY_TEMPLATE,
};

const AddGatewayModal = ({ onClose, onSaved }) => {
  const [form, setForm] = useState(INITIAL_GATEWAY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null); // { gateway, apiKey }
  const notify = useNotify();

  const set = useCallback((field) => (e) => {
    const value = typeof e === "string" ? e : (e?.target?.type === "checkbox" ? e.target.checked : e.target.value);
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const apiKey = generateIgnitionApiKey();
    try {
      const gateway = await createIgnitionGateway({
        name: form.name.trim(),
        api_key: apiKey,
        history_mode: form.historyMode,
        // Recording template only means something in Stored mode — the
        // section is hidden for Federated, so nothing was configured to send.
        ...(form.historyMode === "stored"
          ? { historian_enabled: form.historianEnabled, ...buildHistorianTemplatePayload(form) }
          : {}),
      });
      onSaved();
      notify({ type: "success", title: "Gateway created", message: `"${form.name.trim()}" added.` });
      setCreated({ gateway, apiKey });
    } catch (err) {
      setError(errorMessage(err, "Could not create gateway."));
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    return (
      <TokenReveal
        title="Gateway created"
        subtitle="Connect your Ignition gateway with these values."
        apiKey={created.apiKey}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 460 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">New Ignition Gateway</div>
            <div className="gwModalSubtitle">Register a gateway, then point its Control Seat module at the issued key.</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">Name</label>
            <input
              className="gwInput"
              placeholder="e.g. Plant 1"
              value={form.name}
              onChange={set("name")}
              autoFocus
              required
            />
            <div className="gwHint">Display name shown in the tag tree. Must be unique (case-insensitive).</div>
          </div>
          <div className="gwField">
            <label className="gwLabel">History</label>
            <Select value={form.historyMode} onChange={set("historyMode")} options={HISTORY_MODE_OPTIONS} />
            <div className="gwHint">
              {form.historyMode === "stored"
                ? "Values are recorded into Control Seat's own historian as they arrive."
                : "History is read from the gateway on demand; nothing is stored locally."}
            </div>
          </div>
          {form.historyMode === "stored" && (
            <>
              <div className="gwField">
                <label className="gwHistToggle">
                  <input
                    type="checkbox"
                    className="gwHistToggleInput"
                    checked={form.historianEnabled}
                    onChange={set("historianEnabled")}
                  />
                  <span className={`gwToggle ${form.historianEnabled ? "isOn" : ""}`} aria-hidden="true"><span /></span>
                  Record history
                </label>
                <div className="gwHint">Records this gateway's tags to Control Seat's historian as values arrive.</div>
              </div>
              {form.historianEnabled && <HistorianDefaultsFields form={form} set={set} />}
            </>
          )}
          {error && <div className="gwHint" style={{ color: "var(--gw-danger)" }}>{error}</div>}
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting || !form.name.trim()}>
            {submitting ? "Creating..." : "Create Gateway"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Rename (name only — never touches the key / connection) ───────────

const RenameModal = ({ gateway, onClose, onSaved }) => {
  const [name, setName] = useState(gateway.name || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const notify = useNotify();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      // PATCH name ONLY — omitting api_key leaves the key (and the live
      // connection) untouched; a rename must not reconnect the gateway.
      await updateIgnitionGateway(gateway.id, { name: name.trim() });
      onSaved();
      notify({ type: "success", title: "Renamed", message: `Gateway renamed to "${name.trim()}".` });
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Could not rename gateway."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 440 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">Rename Gateway</div>
            <div className="gwModalSubtitle">Updates the display name only; the connection is unaffected.</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">Name</label>
            <input className="gwInput" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
            <div className="gwHint">Must be unique (case-insensitive).</div>
          </div>
          {error && <div className="gwHint" style={{ color: "var(--gw-danger)" }}>{error}</div>}
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting || !name.trim() || name.trim() === gateway.name}>
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Rotate key (confirm → new token reveal; reconnects the module) ────

const RotateKeyModal = ({ gateway, onClose, onSaved }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [rotated, setRotated] = useState(null); // new apiKey
  const notify = useNotify();

  const handleRotate = async () => {
    setSubmitting(true);
    setError("");
    const apiKey = generateIgnitionApiKey();
    try {
      // PATCH api_key ONLY. The key change drops the live connection until
      // the module reconnects with the new key.
      await updateIgnitionGateway(gateway.id, { api_key: apiKey });
      onSaved();
      notify({ type: "success", title: "Key rotated", message: "A new API key was generated." });
      setRotated(apiKey);
    } catch (err) {
      setError(errorMessage(err, "Could not rotate key."));
    } finally {
      setSubmitting(false);
    }
  };

  if (rotated) {
    return (
      <TokenReveal
        title="Key rotated"
        subtitle="Update the module with the new key to reconnect."
        apiKey={rotated}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="gwOverlay" onClick={onClose}>
      <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 440 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">Rotate API Key</div>
            <div className="gwModalSubtitle">Issues a new key for this gateway.</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
            Rotate the key for <strong>{gateway.name || gateway.id}</strong>? The current key stops working
            immediately and the connection drops until the module is updated with the new key.
          </p>
          {error && <div className="gwHint" style={{ color: "var(--gw-danger)", marginTop: 10 }}>{error}</div>}
        </div>
        <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="button" onClick={handleRotate} disabled={submitting}>
            {submitting ? "Rotating..." : "Rotate Key"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── History settings (mode + recording template) ──────────────────────

const HistoryModal = ({ gateway, onClose, onSaved }) => {
  const originalMode = gateway.history_mode || "stored";
  const [form, setForm] = useState(() => ({
    historyMode: originalMode,
    historianEnabled: !!gateway.historian_enabled,
    ...templateFormFromRecord(gateway),
  }));
  // Tracks whether the operator touched the recording template itself (as
  // opposed to just the History mode select). A pure mode flip must submit
  // no historian_* fields at all — the server leaves the template row
  // completely untouched in that case — so a stored -> federated -> stored
  // round trip can't silently zero it out.
  const [templateTouched, setTemplateTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const notify = useNotify();

  const set = useCallback((field) => (e) => {
    const value = typeof e === "string" ? e : (e?.target?.type === "checkbox" ? e.target.checked : e.target.value);
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setTemplateField = useCallback((field) => (e) => {
    setTemplateTouched(true);
    set(field)(e);
  }, [set]);

  const save = async () => {
    setSubmitting(true);
    setError("");
    try {
      await updateIgnitionGateway(gateway.id, {
        history_mode: form.historyMode,
        ...(templateTouched
          ? { historian_enabled: form.historianEnabled, ...buildHistorianTemplatePayload(form) }
          : {}),
      });
      onSaved();
      notify({ type: "success", title: "History settings updated", message: `"${gateway.name || gateway.id}" updated.` });
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Could not update history settings."));
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mode flips are bulk operations on every tag bound to this gateway
    // (D7) — never silent. Confirm before applying.
    if (form.historyMode !== originalMode) {
      setConfirming(true);
      return;
    }
    save();
  };

  if (confirming) {
    return (
      <div className="gwOverlay" onClick={onClose}>
        <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 440 }}>
          <div className="gwModalHeader">
            <div>
              <div className="gwModalTitle">Confirm History Change</div>
              <div className="gwModalSubtitle">{gateway.name || gateway.id}</div>
            </div>
            <button className="gwModalClose" type="button" onClick={onClose}>
              <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
            </button>
          </div>
          <div className="gwModalBody">
            <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
              {form.historyMode === "federated"
                ? "Switching to Federated will disable recording on this gateway's bound tags. History will only be available live from the gateway."
                : templateTouched
                  ? "Switching to Stored will enable recording on this gateway's bound tags using the recording template below."
                  : "Switching to Stored will resume recording on this gateway's bound tags using its existing recording template."}
            </p>
            {error && <div className="gwHint" style={{ color: "var(--gw-danger)", marginTop: 10 }}>{error}</div>}
          </div>
          <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
            <button className="gwGhostBtn" type="button" onClick={() => setConfirming(false)} disabled={submitting}>Back</button>
            <button className="gwPrimaryBtn" type="button" onClick={save} disabled={submitting}>
              {submitting ? "Saving..." : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 460 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">History Settings</div>
            <div className="gwModalSubtitle">{gateway.name || gateway.id}</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">History</label>
            <Select value={form.historyMode} onChange={set("historyMode")} options={HISTORY_MODE_OPTIONS} />
            <div className="gwHint">
              {form.historyMode === "stored"
                ? "Values are recorded into Control Seat's own historian as they arrive."
                : "History is read from the gateway on demand; nothing is stored locally."}
            </div>
          </div>
          {form.historyMode === "stored" && (
            <>
              <div className="gwField">
                <label className="gwHistToggle">
                  <input
                    type="checkbox"
                    className="gwHistToggleInput"
                    checked={form.historianEnabled}
                    onChange={setTemplateField("historianEnabled")}
                  />
                  <span className={`gwToggle ${form.historianEnabled ? "isOn" : ""}`} aria-hidden="true"><span /></span>
                  Record history
                </label>
                <div className="gwHint">
                  Records this gateway's tags to Control Seat's historian as values arrive. Applies to existing and
                  future imported tags; changing it re-applies to all of this gateway's tags.
                </div>
              </div>
              {form.historianEnabled && <HistorianDefaultsFields form={form} set={setTemplateField} />}
            </>
          )}
          {error && <div className="gwHint" style={{ color: "var(--gw-danger)" }}>{error}</div>}
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting}>
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────

export const IgnitionGatewaysPage = () => {
  const canConfigure = usePermission("configure");
  const [gateways, setGateways] = useState([]);
  const [info, setInfo] = useState({}); // id → { state, tagCount }
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [rotateTarget, setRotateTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});
  const notify = useNotify();

  // Fan out state + tag-count fetches with allSettled so a slow gateway
  // can't block the list, and preserve the prior value on a transient miss.
  const fetchAll = useCallback(async () => {
    try {
      const list = (await listIgnitionGateways()) || [];
      setGateways(list);
      const results = await Promise.allSettled(
        list.map(async (g) => {
          const [st, ct] = await Promise.allSettled([
            getIgnitionGatewayState(g.id),
            getIgnitionGatewayTagCount(g.id),
          ]);
          return [g.id, {
            state: st.status === "fulfilled" ? st.value : null,
            tagCount: ct.status === "fulfilled" ? ct.value : null,
          }];
        })
      );
      setInfo((prev) => {
        const next = { ...prev };
        for (const r of results) {
          if (r.status === "fulfilled") {
            const [id, data] = r.value;
            next[id] = { ...next[id], ...data };
          }
        }
        const liveIds = new Set(list.map((g) => g.id));
        for (const id of Object.keys(next)) {
          if (!liveIds.has(id)) delete next[id];
        }
        return next;
      });
    } catch (err) {
      notify({ type: "error", title: "Error", message: errorMessage(err, "Failed to load gateways") });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchAll]);

  const openMenu = (e, id) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuStyle({ position: "fixed", top: `${Math.round(r.bottom + 4)}px`, right: `${Math.max(12, Math.round(window.innerWidth - r.right))}px` });
    setMenuOpen(menuOpen === id ? null : id);
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      const menu = document.querySelector(".gwProjectMenu");
      if (menu?.contains(e.target)) return;
      setMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteIgnitionGateway(deleteTarget.id);
      notify({ type: "success", title: "Removed", message: `Gateway "${deleteTarget.name || deleteTarget.id}" deleted.` });
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      notify({ type: "error", title: "Delete Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setDeleting(false);
    }
  };

  const hasGateways = gateways.length > 0;
  const cfgTitle = canConfigure ? undefined : "Requires configure permission";

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Ignition</div>
            <div className="gwPageSubtitle">
              {hasGateways
                ? `${gateways.length} gateway${gateways.length !== 1 ? "s" : ""} configured`
                : "Connect Ignition gateways to stream tags and federate history into Control Seat."}
            </div>
          </div>
          <button
            className="gwPrimaryBtn"
            type="button"
            onClick={() => setShowAdd(true)}
            disabled={!canConfigure}
            title={cfgTitle}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
            <span>Add Gateway</span>
          </button>
        </div>

        {loading && !hasGateways && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>Loading gateways...</div>
        )}

        {!loading && !hasGateways && (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">No gateways yet</div>
            <div className="gwEmptyBody">Add an Ignition gateway to start streaming its tags into Control Seat.</div>
            <div className="gwEmptyActions">
              <button
                className="gwPrimaryBtn"
                type="button"
                onClick={() => setShowAdd(true)}
                disabled={!canConfigure}
                title={cfgTitle}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                <span>Add Gateway</span>
              </button>
            </div>
          </div>
        )}

        {hasGateways && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Gateways</div>
            <div className="gwHint" style={{ margin: "2px 0 8px" }}>
              While connected, the module streams value changes for imported tags (read-only); history and alarms are read on demand.
            </div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {gateways.map((g) => {
                const meta = info[g.id] || {};
                const st = meta.state;
                // last_error_msg is set only for genuine errors (a clean shutdown
                // or gateway restart clears it), so promote the dot to "error".
                const status = st?.last_error_msg ? "error" : (st?.status || "offline");
                const colors = STATUS_COLORS[status] || STATUS_COLORS.offline;
                return (
                  <div key={g.id}>
                    <div className="umUserRow">
                      <div
                        style={{
                          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                          background: colors.bg,
                          border: `1px solid ${colors.border}`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 700, color: colors.color,
                        }}
                      >
                        IG
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span className="umUserName">{g.name || g.id}</span>
                          <StatusDot status={status} />
                        </div>
                        <div className="umUserMeta">
                          {formatLastConnected(st?.last_connected_at)}
                          {meta.tagCount != null && <> &middot; {meta.tagCount} tag{meta.tagCount !== 1 ? "s" : ""}</>}
                          {st?.last_error_msg ? (
                            <span style={{ color: "var(--gw-danger)" }} title={st.last_error_msg}> &middot; {st.last_error_msg}</span>
                          ) : status === "offline" && st?.last_connected_at ? (
                            <span style={{ color: "var(--gw-muted)" }}> &middot; Disconnected</span>
                          ) : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="gwIconButton"
                        style={{ flexShrink: 0 }}
                        onClick={(e) => openMenu(e, g.id)}
                      >
                        <img src={moreIcon} alt="More" className="gwIcon" style={{ width: 16, height: 16 }} />
                      </button>
                      {menuOpen === g.id && (
                        <div className="gwProjectMenu" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                          <button
                            className="gwProjectMenuItem"
                            type="button"
                            disabled={!canConfigure}
                            title={cfgTitle}
                            onClick={() => { setMenuOpen(null); setRenameTarget(g); }}
                          >
                            Rename
                          </button>
                          <button
                            className="gwProjectMenuItem"
                            type="button"
                            disabled={!canConfigure}
                            title={cfgTitle}
                            onClick={() => { setMenuOpen(null); setRotateTarget(g); }}
                          >
                            Rotate key
                          </button>
                          <button
                            className="gwProjectMenuItem"
                            type="button"
                            disabled={!canConfigure}
                            title={cfgTitle}
                            onClick={() => { setMenuOpen(null); setHistoryTarget(g); }}
                          >
                            History
                          </button>
                          <div className="gwProfileDivider" />
                          <button
                            className="gwProjectMenuItem gwProjectMenuDanger"
                            type="button"
                            disabled={!canConfigure}
                            title={cfgTitle}
                            onClick={() => { setMenuOpen(null); setDeleteTarget(g); }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {showAdd && <AddGatewayModal onClose={() => setShowAdd(false)} onSaved={fetchAll} />}
      {renameTarget && <RenameModal gateway={renameTarget} onClose={() => setRenameTarget(null)} onSaved={fetchAll} />}
      {rotateTarget && <RotateKeyModal gateway={rotateTarget} onClose={() => setRotateTarget(null)} onSaved={fetchAll} />}
      {historyTarget && <HistoryModal gateway={historyTarget} onClose={() => setHistoryTarget(null)} onSaved={fetchAll} />}

      {deleteTarget && (
        <div className="gwOverlay" onClick={() => setDeleteTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 440 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove Gateway</div>
                <div className="gwModalSubtitle">Bound tags lose their source.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Remove gateway <strong>{deleteTarget.name || deleteTarget.id}</strong>? Its tags are unbound and
                go QualityBad, history federation from this gateway stops, and the module can no longer connect
                with its current key.
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove Gateway"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
